<template>
  <div class="index-trend page-shell">
    <!-- 指数行情卡：最新点位 + 当日涨跌，点击即选入/移出对比 -->
    <div class="index-cards" v-if="indexes.length">
      <div
        v-for="item in indexes"
        :key="item.code"
        class="index-card"
        :class="{ 'is-active': selected.includes(item.code) }"
        @click="toggleIndex(item.code)"
      >
        <div class="idx-name" :title="`${item.name}（${item.firstDate} 起）`">{{ item.name }}</div>
        <div class="idx-close">{{ fmtNum(item.close, 2) }}</div>
        <div class="idx-chg" :class="chgClass(item.pctChg)">{{ fmtPct(item.pctChg) }}</div>
      </div>
    </div>

    <!-- 工具栏：指数多选 + 快捷区间 + 自定义区间 -->
    <div class="toolbar">
      <el-select
        v-model="selected"
        size="small"
        multiple
        filterable
        collapse-tags
        placeholder="请选择指数（可多选，最多 6 个）"
        style="width: 340px"
      >
        <el-option
          v-for="item in indexes"
          :key="item.code"
          :label="item.name"
          :value="item.code"
          :disabled="isIndexDisabled(item.code)"
        />
      </el-select>

      <el-radio-group v-model="quickRange" size="small" @change="onQuickRange">
        <el-radio-button v-for="q in QUICK_RANGES" :key="q.key" :label="q.key">{{ q.label }}</el-radio-button>
      </el-radio-group>

      <el-date-picker
        v-model="dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        :picker-options="pickerOptions"
        :clearable="false"
      />

      <el-button type="primary" size="small" :loading="loading" @click="onQuery">查询</el-button>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <!-- 区间统计卡：每选中指数一张 -->
    <div v-if="stats.length" class="stats-grid">
      <div v-for="s in stats" :key="s.code" class="stat-card">
        <div class="stat-head">
          <span class="stat-name">{{ s.name }}</span>
          <span class="stat-ret" :class="s.periodReturn >= 0 ? 'up' : 'down'">
            {{ fmtPct(s.periodReturn * 100) }}
          </span>
        </div>
        <div class="stat-rows">
          <div class="stat-row"><span>年化收益</span><b :class="s.annualized >= 0 ? 'up' : 'down'">{{ fmtPct(s.annualized * 100) }}</b></div>
          <div class="stat-row"><span>最大回撤</span><b class="down">{{ fmtPct(s.maxDrawdown * 100) }}</b></div>
          <div class="stat-row"><span>年化波动</span><b>{{ fmtPct(s.volatility * 100) }}</b></div>
          <div class="stat-row"><span>区间最高</span><b>{{ fmtNum(s.high, 2) }}</b></div>
          <div class="stat-row"><span>区间最低</span><b>{{ fmtNum(s.low, 2) }}</b></div>
        </div>
      </div>
    </div>

    <div
      ref="chart"
      class="chart-box"
      v-loading="loading"
      element-loading-text="查询中..."
    />

    <div v-if="queried && !loading && !error && !stats.length" class="empty-tip">
      无数据，请选择指数并确认日期区间
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart, CandlestickChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { fetchIndexList, fetchIndexTrend } from '../utils/indexApi.js'

echarts.use([
  LineChart,
  CandlestickChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
  CanvasRenderer,
])

const MAX_INDEXES = 6
const LINE_COLORS = ['#409EFF', '#E6A23C', '#F56C6C', '#67C23A', '#8B5CF6', '#00C2B3']
// A 股口径：涨红跌绿
const CANDLE_UP = '#ec0000'
const CANDLE_DOWN = '#00a800'

const QUICK_RANGES = [
  { key: '1m', label: '近1月' },
  { key: '3m', label: '近3月' },
  { key: '6m', label: '近6月' },
  { key: 'ytd', label: '今年' },
  { key: '1y', label: '近1年' },
  { key: '3y', label: '近3年' },
  { key: '5y', label: '近5年' },
  { key: 'all', label: '全部' },
]

function fmtDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 快捷区间 → [start, end]；all 返回 null（不过滤） */
function rangeToDates(key) {
  if (key === 'all') return null
  const end = new Date()
  if (key === 'ytd') return [`${end.getFullYear()}-01-01`, fmtDate(end)]
  const months = { '1m': 1, '3m': 3, '6m': 6, '1y': 12, '3y': 36, '5y': 60 }[key]
  const start = new Date(end)
  start.setMonth(start.getMonth() - months)
  return [fmtDate(start), fmtDate(end)]
}

/** 简单移动平均（不足窗口期返回 null）；结果保留 2 位小数，避免浮点累加出现 4766.483900000001 */
function movingAvg(values, window) {
  const out = []
  let sum = 0
  for (let i = 0; i < values.length; i += 1) {
    sum += values[i]
    if (i >= window) sum -= values[i - window]
    out.push(i >= window - 1 ? Number((sum / window).toFixed(2)) : null)
  }
  return out
}

/** 点位数值统一口径：2 位小数 + 千分位（tooltip / 坐标轴 / 统计卡共用） */
function fmtPx(v) {
  const n = Number(v)
  if (v == null || v === '' || !Number.isFinite(n)) return '--'
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function escapeHtml(str) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }
  return String(str == null ? '' : str).replace(/[&<>"]/g, (c) => map[c])
}

/** 单选 K 线 tooltip：开/收/高/低 + 均线，数值 2 位小数、右对齐 */
function candleTooltip(params) {
  const list = Array.isArray(params) ? params : [params]
  if (!list.length) return ''
  const tdRight = 'style="text-align:right;padding-left:18px;font-variant-numeric:tabular-nums"'
  const rows = list.map((p) => {
    const color = (p.color && p.color.color) || p.color || '#606266'
    const dot = `<span style="display:inline-block;width:8px;height:8px;margin-right:6px;border-radius:50%;background:${color}"></span>`
    if (p.seriesType === 'candlestick') {
      // p.value 形如 [开, 收, 低, 高]（部分版本含类目维，取末尾 4 个数值兜底）
      const nums = (Array.isArray(p.value) ? p.value : []).map(Number).filter((n) => Number.isFinite(n))
      const [open, close, low, high] = nums.slice(-4)
      return (
        `<tr><td colspan="2" style="padding-top:4px">${dot}${escapeHtml(p.seriesName)}</td></tr>` +
        `<tr><td style="color:#909399">开</td><td ${tdRight}>${fmtPx(open)}</td></tr>` +
        `<tr><td style="color:#909399">收</td><td ${tdRight}>${fmtPx(close)}</td></tr>` +
        `<tr><td style="color:#909399">高</td><td ${tdRight}>${fmtPx(high)}</td></tr>` +
        `<tr><td style="color:#909399">低</td><td ${tdRight}>${fmtPx(low)}</td></tr>`
      )
    }
    return `<tr><td style="padding-top:4px">${dot}${escapeHtml(p.seriesName)}</td><td ${tdRight}>${fmtPx(p.value)}</td></tr>`
  })
  const head = escapeHtml(list[0].axisValueLabel || list[0].axisValue || '')
  return (
    '<table style="border-collapse:collapse">' +
    `<tr><td colspan="2" style="padding-bottom:2px;font-weight:600">${head}</td></tr>` +
    rows.join('') +
    '</table>'
  )
}

/**
 * 多指数归一化折线的 tooltip 工厂：归一化读数 + 区间累计涨跌 + 原始点位
 * rawByIndex 与 series 同序，元素为与 x 轴 dates 对齐的原始收盘价（缺失为 null）
 */
function makeLineTooltip(rawByIndex) {
  const tdRight = 'style="text-align:right;padding-left:16px;font-variant-numeric:tabular-nums"'
  return function lineTooltip(params) {
    const list = Array.isArray(params) ? params : [params]
    const rows = []
    for (const p of list) {
      const idx = Number(p.value)
      if (!Number.isFinite(idx)) continue
      const color = (p.color && p.color.color) || p.color || '#606266'
      const dot = `<span style="display:inline-block;width:8px;height:8px;margin-right:6px;border-radius:50%;background:${color}"></span>`
      const raw = rawByIndex[p.seriesIndex]?.[p.dataIndex]
      const chg = idx - 100
      rows.push(
        `<tr><td>${dot}${escapeHtml(p.seriesName)}</td>` +
          `<td ${tdRight}>${idx.toFixed(2)}</td>` +
          `<td style="text-align:right;padding-left:12px;color:${chg >= 0 ? CANDLE_UP : CANDLE_DOWN}">${chg > 0 ? '+' : ''}${chg.toFixed(2)}%</td>` +
          `<td style="text-align:right;padding-left:14px;color:#909399">原始 ${raw == null ? '--' : fmtPx(raw)}</td></tr>`
      )
    }
    if (!rows.length) return ''
    const head = escapeHtml(list[0].axisValueLabel || list[0].axisValue || '')
    return (
      '<table style="border-collapse:collapse">' +
      `<tr><td colspan="4" style="padding-bottom:2px;font-weight:600">${head}</td></tr>` +
      rows.join('') +
      '</table>'
    )
  }
}

/** 十字光标轴标签：Y 轴数值统一 2 位小数 */
const POINTER_LABEL = {
  formatter(pp) {
    return pp && pp.axisDimension === 'y' ? fmtPx(pp.value) : pp && pp.value
  },
}

export default {
  name: 'IndexTrend',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      QUICK_RANGES,
      indexes: [],
      selected: [],
      quickRange: '1y',
      dateRange: rangeToDates('1y'),
      loading: false,
      queried: false,
      error: '',
      result: null,
      chart: null,
      resizeObserver: null,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        },
      },
    }
  },
  computed: {
    stats() {
      const series = this.result?.series || []
      return series
        .filter((s) => s.bars?.length >= 2)
        .map((s) => this.calcStats(s))
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => this.chart?.resize())
      }
    },
  },
  async created() {
    try {
      this.indexes = await fetchIndexList()
      if (!this.selected.length && this.indexes.length) {
        const preferred = ['000001', '000300', '399006']
        const known = this.indexes.map((x) => x.code)
        this.selected = preferred.filter((c) => known.includes(c))
      }
    } catch (err) {
      this.error = err.message || '加载指数列表失败'
    }
    if (this.selected.length) await this.onQuery()
  },
  mounted() {
    this.initChart()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize())
      if (this.$refs.chart) this.resizeObserver.observe(this.$refs.chart)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    isIndexDisabled(code) {
      return this.selected.length >= MAX_INDEXES && !this.selected.includes(code)
    },
    chgClass(v) {
      if (v == null || !Number.isFinite(Number(v))) return ''
      return Number(v) >= 0 ? 'up' : 'down'
    },
    fmtNum(v, digits = 2) {
      if (v == null || v === '' || !Number.isFinite(Number(v))) return '--'
      return Number(v).toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    },
    fmtPct(v) {
      if (v == null || v === '' || !Number.isFinite(Number(v))) return '--'
      const n = Number(v)
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
    },
    toggleIndex(code) {
      const i = this.selected.indexOf(code)
      if (i >= 0) {
        this.selected.splice(i, 1)
      } else {
        if (this.selected.length >= MAX_INDEXES) {
          this.$message.warning(`最多同时对比 ${MAX_INDEXES} 个指数`)
          return
        }
        this.selected.push(code)
      }
      this.onQuery()
    },
    onQuickRange(key) {
      this.dateRange = rangeToDates(key)
      this.onQuery()
    },
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption({
        title: {
          text: '请选择指数后查询',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 },
        },
      })
    },
    /** 区间统计：涨跌幅 / 年化 / 最大回撤 / 年化波动 / 高低点 */
    calcStats(s) {
      const bars = s.bars
      const closes = bars.map((b) => b.close)
      const first = closes[0]
      const last = closes[closes.length - 1]
      const periodReturn = last / first - 1
      const n = bars.length
      const annualized = n > 1 ? (last / first) ** (252 / n) - 1 : periodReturn

      let peak = closes[0]
      let maxDrawdown = 0
      for (const c of closes) {
        if (c > peak) peak = c
        const dd = c / peak - 1
        if (dd < maxDrawdown) maxDrawdown = dd
      }

      const rets = []
      for (let i = 1; i < n; i += 1) rets.push(closes[i] / closes[i - 1] - 1)
      const mean = rets.reduce((a, b) => a + b, 0) / (rets.length || 1)
      const variance = rets.reduce((a, b) => a + (b - mean) ** 2, 0) / (rets.length || 1)
      const volatility = Math.sqrt(variance) * Math.sqrt(252)

      const validLow = (b) => (b.low != null && b.low > 0 ? b.low : b.close)
      return {
        code: s.code,
        name: s.name,
        periodReturn,
        annualized,
        maxDrawdown,
        volatility,
        high: Math.max(...bars.map((b) => (b.high != null && b.high > 0 ? b.high : b.close))),
        low: Math.min(...bars.map(validLow)),
      }
    },
    /** 单选 → 日K蜡烛 + 均线；多选 → 归一化（起点=100）叠加 */
    renderChart(result) {
      this.initChart()
      if (!this.chart || !result) return
      const dates = result.dates || []
      const series = result.series || []

      if (!dates.length || !series.length) {
        this.chart.clear()
        return
      }

      const baseOption = {
        title: { show: false },
        color: LINE_COLORS,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line', label: POINTER_LABEL },
        },
        legend: { type: 'scroll', top: 0 },
        grid: { left: 56, right: 24, top: 48, bottom: 64 },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 10 },
        ],
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: series.length === 1,
          axisLabel: {
            formatter(value) {
              const parts = String(value).split('-')
              return parts.length === 3 ? `${parts[1]}-${parts[2]}` : value
            },
          },
        },
      }

      if (series.length === 1) {
        // 单指数：日 K + MA5/MA10/MA20（boundaryGap=true 才不让蜡烛贴边）
        const s = series[0]
        const byDate = new Map(s.bars.map((b) => [b.date, b]))
        const bars = dates.map((d) => {
          const b = byDate.get(d)
          if (!b) return ['-', '-', '-', '-']
          return [b.open, b.close, b.low, b.high]
        })
        const closes = dates.map((d) => byDate.get(d)?.close ?? null)
        const maSeries = [5, 10, 20].map((w, i) => ({
          name: `MA${w}`,
          type: 'line',
          data: movingAvg(closes, w),
          showSymbol: false,
          smooth: true,
          lineStyle: { width: 1, opacity: 0.9 },
          itemStyle: { color: ['#E6A23C', '#409EFF', '#8B5CF6'][i] },
          color: ['#E6A23C', '#409EFF', '#8B5CF6'][i],
        }))
        this.chart.setOption({
          ...baseOption,
          legend: { type: 'scroll', top: 0, data: ['MA5', 'MA10', 'MA20'] },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross', label: POINTER_LABEL },
            formatter: candleTooltip,
          },
          yAxis: {
            type: 'value',
            scale: true,
            name: s.name,
            axisLabel: { formatter: (v) => Number(v).toLocaleString('zh-CN', { maximumFractionDigits: 0 }) },
          },
          series: [
            {
              name: s.name,
              type: 'candlestick',
              data: bars,
              itemStyle: { color: CANDLE_UP, color0: CANDLE_DOWN, borderColor: CANDLE_UP, borderColor0: CANDLE_DOWN },
            },
            ...maSeries,
          ],
        }, true)
      } else {
        // 多指数：归一化（区间首个有效收盘=100）叠加对比
        // 先按 series 顺序取原始收盘（与 x 轴 dates 对齐），供折线换算 + tooltip 显示原始点位
        const rawByIndex = series.map((s) => {
          const byDate = new Map(s.bars.map((b) => [b.date, b.close]))
          return dates.map((d) => (byDate.has(d) ? byDate.get(d) : null))
        })
        const lines = series.map((s, i) => {
          const firstClose = s.bars[0]?.close
          const raw = rawByIndex[i]
          const data = dates.map((d, j) => {
            const c = raw[j]
            if (c == null || !firstClose) return null
            return Number(((c / firstClose) * 100).toFixed(2))
          })
          return { name: s.name, type: 'line', data, showSymbol: false, smooth: true, connectNulls: true }
        })
        this.chart.setOption({
          ...baseOption,
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', label: POINTER_LABEL },
            formatter: makeLineTooltip(rawByIndex),
          },
          yAxis: {
            type: 'value',
            scale: true,
            name: '归一化（起点=100）',
            axisLabel: { formatter: (v) => Number(v).toLocaleString('zh-CN', { maximumFractionDigits: 0 }) },
          },
          series: lines,
        }, true)
      }
      this.$nextTick(() => this.chart?.resize())
    },
    async onQuery() {
      if (!this.selected.length) {
        this.result = null
        this.chart?.clear()
        this.chart?.setOption({
          title: {
            text: '请选择指数后查询',
            left: 'center',
            top: 'middle',
            textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 },
          },
        })
        return
      }
      const start = this.dateRange?.[0] || null
      const end = this.dateRange?.[1] || null

      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const data = await fetchIndexTrend({ codes: this.selected, start, end })
        this.result = data
        this.renderChart(data)
      } catch (err) {
        this.result = null
        this.error = err.message || '查询失败'
        this.chart?.clear()
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.index-trend {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
}

.index-cards {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.index-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  padding: 8px 10px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-width: 0;
}

.index-card:hover {
  border-color: #c6e2ff;
}

.index-card.is-active {
  border-color: #409eff;
  box-shadow: 0 0 0 1px #409eff inset;
}

.idx-name {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.idx-close {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin-top: 2px;
}

.idx-chg {
  font-size: 12px;
  margin-top: 2px;
}

.idx-chg.up {
  color: #f56c6c;
}

.idx-chg.down {
  color: #67c23a;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.stat-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  padding: 10px 12px;
}

.stat-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 1px solid #f2f6fc;
  padding-bottom: 6px;
  margin-bottom: 6px;
}

.stat-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.stat-ret {
  font-size: 15px;
  font-weight: 500;
}

.stat-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
}

.stat-row b {
  font-weight: 500;
  color: #303133;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.error-alert {
  margin-bottom: 12px;
}

.chart-box {
  flex: 1;
  min-height: 460px;
  width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.empty-tip {
  padding: 24px 0;
  color: #909399;
  text-align: center;
}

@media (max-width: 768px) {
  .index-cards {
    display: flex;
    overflow-x: auto;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
  }

  .index-card {
    flex: 0 0 88px;
  }

  .idx-close {
    font-size: 14px;
  }

  .toolbar .el-select {
    width: 100% !important;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-box {
    min-height: 340px;
  }
}
</style>
